import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  // Scroll to top on navigation (pathname change)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Scroll to top on page load/refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
}

// Visitor tracking component
function VisitorTracker() {
  const location = useLocation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const trackVisit = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/track-visit?page=${encodeURIComponent(location.pathname)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Error tracking visit:', error);
      }
    };

    trackVisit();
  }, [location.pathname, BACKEND_URL]);

  return null;
}
import './App.css';

// Import page components
import { ServicesPage } from './pages/Services';
import Testimonials from './pages/Testimonials';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import { FleetPage } from './pages/Fleet';
import { CareersPage } from './pages/Careers';
import { ContactPage } from './pages/Contact';
import { GetQuotePage } from './pages/GetQuote';
import { CompanyDriverApplication, OwnerOperatorApplication } from './pages/Applications';
import { AdminLogin, AdminDashboard } from './pages/Admin';
import PortalLogin from './pages/Portal';
import PortalDashboard from './pages/PortalDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center">
            <img src="/main_logo.png" alt="Rob & Roberly Transport" className="h-16" />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 ml-8">
            <nav className="flex space-x-6 items-center" data-testid="desktop-nav">
              <Link to="/" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/') ? 'text-blue-600' : ''}`}>Home</Link>
              <Link to="/about" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/about') ? 'text-blue-600' : ''}`}>About Us</Link>
              <Link to="/services" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/services') ? 'text-blue-600' : ''}`}>Services</Link>
              <Link to="/fleet" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/fleet') ? 'text-blue-600' : ''}`}>Fleet</Link>
              <Link to="/careers" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/careers') ? 'text-blue-600' : ''}`}>Careers</Link>
              <Link to="/contact" className={`text-blue-900 hover:text-blue-600 font-medium whitespace-nowrap ${isActive('/contact') ? 'text-blue-600' : ''}`}>Contact Us</Link>
              <Link to="/portal" className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition ${isActive('/portal') ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900 hover:bg-blue-100'}`}>Customer Portal</Link>
            </nav>
            
            {/* Social Media Icons - Colorful */}
            <div className="flex items-center gap-3 border-l border-gray-300 pl-6">
              <a href="https://facebook.com/RoberlyTRANSPORT/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 transition" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://instagram.com/robandroberlytransport" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 transition" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://wa.me/19089520250" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 transition" aria-label="WhatsApp">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </div>
            
            <Link to="/get-quote" className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition" data-testid="get-quote-btn">Get Instant Quote</Link>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden pb-4" data-testid="mobile-nav">
            <Link to="/" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">Home</Link>
            <Link to="/about" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">About Us</Link>
            <Link to="/services" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">Services</Link>
            <Link to="/fleet" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">Fleet</Link>
            <Link to="/careers" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">Careers</Link>
            <Link to="/contact" className="block py-2 text-blue-900 hover:text-blue-600 whitespace-nowrap">Contact Us</Link>
            <Link to="/portal" className="block py-2 px-3 my-1 rounded-lg bg-blue-50 text-blue-900 hover:bg-blue-100 font-medium whitespace-nowrap">Customer Portal</Link>
            <Link to="/get-quote" className="block py-2 text-blue-600 font-medium whitespace-nowrap">Get Instant Quote</Link>
          </nav>
        )}
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-blue-900 text-white py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <Link to="/">
              <img src="/logo_footer.png" alt="Rob & Roberly Transport" className="h-20 mb-4 cursor-pointer hover:opacity-80 transition" />
            </Link>
            <p className="text-gray-400 text-sm mb-2">Your trusted partner in container drayage across the Northeast.</p>
            <p className="text-gray-300 text-base font-medium">Serving: NJ, NY, CT, PA, DE</p>
          </div>
          <div>
            <h3 className="font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
              <li><Link to="/services" className="hover:text-white">Services</Link></li>
              <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold mb-4">Services</h3>
            <ul className="space-y-2 text-gray-400">
              <li>Container Drayage</li>
              <li>Warehousing</li>
              <li>Sea Freight</li>
              <li>Reefer Container</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold mb-4">Contact Info</h3>
            <ul className="space-y-2 text-gray-400">
              <li>Phone: <a href="tel:+19089520250" className="hover:text-white transition">(908) 952-0250</a></li>
              <li>WhatsApp: <a href="https://wa.me/19089520250" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">(908) 952-0250</a></li>
              <li>Email: <a href="mailto:Rates@robandroberlytransport.com" className="hover:text-white transition">Rates@robandroberlytransport.com</a></li>
              <li>Address: <a href="https://www.google.com/maps/search/?api=1&query=45+East+Milton+Ave+Rahway+New+Jersey+07065" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">45 East Milton Ave<br/>Rahway, New Jersey 07065</a></li>
            </ul>
            
            {/* Social Media Icons - Colorful */}
            <div className="mt-4">
              <h4 className="font-bold mb-3">Follow Us</h4>
              <div className="flex gap-4">
                <a href="https://facebook.com/RoberlyTRANSPORT/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 transition" aria-label="Facebook">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://instagram.com/robandroberlytransport" target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:text-pink-400 transition" aria-label="Instagram">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://wa.me/19089520250" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition" aria-label="WhatsApp">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p className="text-sm mb-2">
            <span className="font-semibold text-gray-300">DOT: 3211632</span>
            <span className="mx-2">|</span>
            <span className="font-semibold text-gray-300">MC: 1003058</span>
          </p>
          <p className="text-sm">&copy; {new Date().getFullYear()} Rob & Roberly Transport LLC. All rights reserved. <Link to="/admin" className="text-gray-800 hover:text-gray-600 ml-4 text-xs">•</Link></p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/19089520250?text=Hi%2C%20I%27d%20like%20to%20request%20a%20quote"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 transition-all duration-300 hover:scale-110 z-50 group"
      aria-label="Chat on WhatsApp"
    >
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
        Chat Now
      </span>
    </a>
  );
};

// Live Availability Banner Component  
const LiveAvailabilityBanner = () => {
  // Check if current date is a major holiday
  const isHoliday = () => {
    const now = new Date();
    const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const month = et.getMonth(); // 0 = January, 11 = December
    const date = et.getDate();
    const day = et.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Major US Holidays (Fixed dates)
    const holidays = [
      { month: 0, date: 1 },    // New Year's Day (January 1)
      { month: 6, date: 4 },    // Independence Day (July 4)
      { month: 11, date: 25 },  // Christmas Day (December 25)
    ];
    
    // Check fixed date holidays
    for (const holiday of holidays) {
      if (month === holiday.month && date === holiday.date) {
        return true;
      }
    }
    
    // Memorial Day (Last Monday of May)
    if (month === 4 && day === 1) {
      const lastMonday = new Date(et.getFullYear(), 4, 31);
      while (lastMonday.getDay() !== 1) {
        lastMonday.setDate(lastMonday.getDate() - 1);
      }
      if (date === lastMonday.getDate()) {
        return true;
      }
    }
    
    // Labor Day (First Monday of September)
    if (month === 8 && day === 1 && date <= 7) {
      return true;
    }
    
    // Thanksgiving (Fourth Thursday of November)
    if (month === 10 && day === 4) {
      const firstThursday = new Date(et.getFullYear(), 10, 1);
      while (firstThursday.getDay() !== 4) {
        firstThursday.setDate(firstThursday.getDate() + 1);
      }
      const fourthThursday = firstThursday.getDate() + 21;
      if (date === fourthThursday) {
        return true;
      }
    }
    
    return false;
  };

  // Check if currently within business hours (Mon-Fri 8AM-5PM ET)
  const isBusinessHours = () => {
    // Check if it's a holiday first
    if (isHoliday()) {
      return false;
    }
    
    const now = new Date();
    const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const day = et.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
    const hour = et.getHours();
    
    // Monday (1) to Friday (5), 8 AM to 5 PM
    return day >= 1 && day <= 5 && hour >= 8 && hour < 17;
  };

  const isOpen = isBusinessHours();

  return (
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 text-center">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-1">
              {isOpen ? (
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
                </span>
              ) : (
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white opacity-50"></span>
              )}
              <span className="text-sm">Currently</span>
            </div>
            <span className="font-bold text-lg">{isOpen ? 'Open' : 'Closed'}</span>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-sm mb-1">Response Time:</span>
            <span className="font-bold text-lg">~15 Minutes</span>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-sm mb-1">Office Hours:</span>
            <span className="font-bold text-lg whitespace-nowrap">Mon-Fri 8AM-5PM (ET)</span>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-sm mb-1">Same-Day</span>
            <span className="font-bold text-lg">Pickup Available</span>
          </div>

          <a 
            href="mailto:Rates@robandroberlytransport.com" 
            className="bg-white text-blue-600 px-6 py-2 rounded-full font-bold hover:bg-blue-50 transition text-sm md:text-base whitespace-nowrap flex flex-col items-center"
          >
            <span className="text-sm font-normal">Email Us Today</span>
            <span className="flex items-center gap-1">
              📧 Rates@robandroberlytransport.com
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  // Disable browser's automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <VisitorTracker />
      <div className="App min-h-screen flex flex-col">
        <Header />
        <LiveAvailabilityBanner />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/fleet" element={<FleetPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/get-quote" element={<GetQuotePage />} />
            <Route path="/apply/company-driver" element={<CompanyDriverApplication />} />
            <Route path="/apply/owner-operator" element={<OwnerOperatorApplication />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/portal" element={<PortalLogin />} />
            <Route path="/portal/dashboard" element={<PortalDashboard />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
      </div>
    </BrowserRouter>
  );
}

// Quick Calculator Widget Component
const QuickCalculatorWidget = () => {
  const [pickup, setPickup] = useState('');
  const [delivery, setDelivery] = useState('');
  const [container, setContainer] = useState("20' Standard");
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDeliverySuggestions, setShowDeliverySuggestions] = useState(false);
  const [filteredPickupLocations, setFilteredPickupLocations] = useState([]);
  const [filteredDeliveryLocations, setFilteredDeliveryLocations] = useState([]);

  // Same locations as RateCalculator for consistency
  const locations = [
    'NY/NJ PORTS', 'NY/NJ TERMINAL', 'NY/NJ TERMINALS',
    'MAHER', 'APM', 'PNCT', 'CCS', 'COLUMBIA', 'CES',
    // NJ Cities
    'Newark, NJ', 'Jersey City, NJ', 'Elizabeth, NJ', 'Edison, NJ', 'Trenton, NJ',
    'Paterson, NJ', 'Woodbridge, NJ', 'Lakewood, NJ', 'Toms River, NJ', 'Clifton, NJ',
    'Camden, NJ', 'Cherry Hill, NJ', 'Passaic, NJ', 'Union City, NJ', 'Old Bridge, NJ',
    'Bayonne, NJ', 'North Bergen, NJ', 'Vineland, NJ', 'New Brunswick, NJ', 'Perth Amboy, NJ',
    'Hoboken, NJ', 'Parsippany, NJ', 'Princeton, NJ', 'Freehold, NJ', 'Morristown, NJ',
    'Dayton, NJ', 'South Brunswick, NJ', 'East Brunswick, NJ', 'Bridgewater, NJ', 'Hackensack, NJ',
    'Fort Lee, NJ', 'Montclair, NJ', 'Atlantic City, NJ', 'Kearny, NJ', 'Linden, NJ',
    // NY Cities
    'New York, NY', 'Brooklyn, NY', 'Buffalo, NY', 'Albany, NY', 'Syracuse, NY', 'Rochester, NY',
    'Yonkers, NY', 'White Plains, NY',
    // PA Cities
    'Philadelphia, PA', 'Pittsburgh, PA', 'Harrisburg, PA', 'Allentown, PA', 'Reading, PA',
    'Scranton, PA', 'Bethlehem, PA', 'Lancaster, PA', 'York, PA', 'Erie, PA',
    'King of Prussia, PA', 'Norristown, PA', 'West Chester, PA', 'Easton, PA',
    // Other States
    'Hartford, CT', 'New Haven, CT', 'Bridgeport, CT', 'Stamford, CT',
    'Wilmington, DE', 'Dover, DE', 'Newark, DE',
    'Baltimore, MD', 'Annapolis, MD',
    'Boston, MA', 'Worcester, MA', 'Springfield, MA',
    'Providence, RI',
    'Richmond, VA', 'Norfolk, VA', 'Virginia Beach, VA'
  ];

  const handlePickupChange = (e) => {
    const value = e.target.value;
    setPickup(value);
    if (value.length > 0) {
      const filtered = locations.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPickupLocations(filtered);
      setShowPickupSuggestions(filtered.length > 0);
    } else {
      setShowPickupSuggestions(false);
    }
  };

  const handleDeliveryChange = (e) => {
    const value = e.target.value;
    setDelivery(value);
    if (value.length > 0) {
      const filtered = locations.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDeliveryLocations(filtered);
      setShowDeliverySuggestions(filtered.length > 0);
    } else {
      setShowDeliverySuggestions(false);
    }
  };

  const selectPickupLocation = (location) => {
    setPickup(location);
    setShowPickupSuggestions(false);
  };

  const selectDeliveryLocation = (location) => {
    setDelivery(location);
    setShowDeliverySuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (pickup) params.set('pickup', pickup);
    if (delivery) params.set('delivery', delivery);
    if (container) params.set('container', container);
    window.location.href = `/get-quote?${params.toString()}`;
  };

  return (
    <section className="py-6 bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Get Your Instant Quote</h2>
            <p className="text-base text-gray-600">Enter your shipment details for a quick estimate</p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Pickup Location</label>
                <input
                  type="text"
                  value={pickup}
                  onChange={handlePickupChange}
                  onFocus={() => {
                    if (pickup.length > 0 && filteredPickupLocations.length > 0) {
                      setShowPickupSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowPickupSuggestions(false), 200)}
                  placeholder="e.g., NY/NJ PORTS"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm"
                />
                {showPickupSuggestions && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredPickupLocations.map((loc, index) => (
                      <div
                        key={index}
                        onClick={() => selectPickupLocation(loc)}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-b-0"
                      >
                        {loc}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Delivery Location</label>
                <input
                  type="text"
                  value={delivery}
                  onChange={handleDeliveryChange}
                  onFocus={() => {
                    if (delivery.length > 0 && filteredDeliveryLocations.length > 0) {
                      setShowDeliverySuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowDeliverySuggestions(false), 200)}
                  placeholder="e.g., Newark, NJ"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm"
                />
                {showDeliverySuggestions && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredDeliveryLocations.map((loc, index) => (
                      <div
                        key={index}
                        onClick={() => selectDeliveryLocation(loc)}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-b-0"
                      >
                        {loc}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Container Size</label>
                <select 
                  value={container}
                  onChange={(e) => setContainer(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm"
                >
                  <option>20' Standard</option>
                  <option>40' Standard</option>
                  <option>40' High Cube</option>
                  <option>45' High Cube</option>
                </select>
              </div>
            </div>
            
            <button 
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition font-bold text-base shadow-md hover:shadow-lg flex items-center justify-center gap-2 group"
            >
              Calculate Full Quote
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </form>
          
          <p className="text-center text-gray-500 text-xs mt-3">
            ⚡ Get detailed pricing in seconds | 📱 No login required | 🎯 Accurate estimates
          </p>
        </div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  // Hero slideshow images
  const heroImages = [
    'https://images.unsplash.com/photo-1590496793907-4d66e2994b4d?q=80&w=2000',
    'https://customer-assets.emergentagent.com/job_shiprate-admin/artifacts/0pd0twai_E12zdomWQAEiSfY.jpg',
    'https://customer-assets.emergentagent.com/job_freightflow-50/artifacts/betpfzun_WhatsApp%20Image%202025-11-09%20at%2022.02.38_22d0caf4.jpg',
    'https://customer-assets.emergentagent.com/job_shiprate-admin/artifacts/gc3y4292_ship-to-shore.jpg'
  ];
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Slideshow effect - change image every 5 seconds (only when playing)
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div data-testid="home-page">
      {/* Hero Section with Animated Slideshow */}
      <section className="relative h-[800px] overflow-hidden">
        {/* Slideshow Images */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: currentImageIndex === index ? 1 : 0,
              zIndex: currentImageIndex === index ? 1 : 0
            }}
          />
        ))}
        
        {/* Overlay - much lighter for clearer images */}
        <div className="absolute inset-0 bg-black bg-opacity-10" style={{zIndex: 2}}></div>
        
        {/* Content with text shadow for better readability */}
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center" style={{zIndex: 3, marginTop: '-80px'}}>
          <div className="text-white max-w-3xl">
            <div className="p-3 sm:p-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 drop-shadow-2xl" data-testid="hero-title" style={{textShadow: '0 4px 8px rgba(0,0,0,0.8)'}}>Your Trusted Partner in Container Drayage</h1>
              <p className="text-lg sm:text-xl md:text-3xl mb-5 drop-shadow-xl leading-relaxed" style={{textShadow: '0 2px 6px rgba(0,0,0,0.8)'}}>Specializing in import and export container drayage across the Northeast.</p>
              <p className="text-base sm:text-lg md:text-2xl mb-6 text-gray-100 drop-shadow-xl leading-relaxed" style={{textShadow: '0 2px 6px rgba(0,0,0,0.8)'}}>With over 8 years of experience, hundreds of satisfied clients, and thousands of containers successfully moved across NJ, NY, CT, PA, and DE.</p>
              <div className="flex gap-3 flex-col sm:flex-row">
                <Link to="/get-quote" className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition font-bold text-lg shadow-xl text-center" data-testid="hero-get-quote-btn">Get Instant Quote</Link>
                <a href="tel:+19089520250" className="bg-blue-900 text-white px-8 py-4 rounded-lg hover:bg-blue-950 transition font-bold text-lg shadow-xl flex items-center justify-center gap-2">
                  📞 Call: (908) 952-0250
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Slideshow Indicators with Play/Pause */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-4" style={{zIndex: 3}}>
          {/* Play/Pause Button */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="bg-white bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all duration-300"
            aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isPlaying ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          
          {/* Slide Indicators */}
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentImageIndex === index 
                  ? 'bg-white w-8' 
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Quick Calculator Widget */}
      <QuickCalculatorWidget />

      {/* Services Overview */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Why Choose Rob & Roberly Transport?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Comprehensive logistics solutions with reliability, efficiency, and customer satisfaction.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition overflow-hidden" data-testid="service-card">
              <img src="https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/uim4b6g7_image.png" alt="Container Drayage" className="w-full h-48 object-cover" />
              <div className="p-6 text-center">
                <h3 className="font-bold text-xl mb-3">Container Drayage</h3>
                <p className="text-gray-600">Expert import & export services from major ports including Newark, Elizabeth, and NY/NJ terminals. We handle 20', 40', and 45' containers with specialized equipment.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition overflow-hidden" data-testid="service-card">
              <img src="https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/jeqrr0bo_image.png" alt="Real-Time Tracking" className="w-full h-48 object-cover" />
              <div className="p-6 text-center">
                <h3 className="font-bold text-xl mb-3">Real-Time Tracking</h3>
                <p className="text-gray-600">Advanced GPS tracking on all vehicles with real-time monitoring. Receive instant updates on pickup, transit, and delivery status via phone, email, or text.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition overflow-hidden" data-testid="service-card">
              <img src="https://customer-assets.emergentagent.com/job_shiprate-admin/artifacts/au1rn2zk_image.png" alt="Experienced Drivers" className="w-full h-48 object-cover" />
              <div className="p-6 text-center">
                <h3 className="font-bold text-xl mb-3">Experienced Drivers</h3>
                <p className="text-gray-600">CDL-certified professional drivers with extensive training in safety, compliance, and customer service. Average experience of 8+ years in container drayage operations.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition overflow-hidden" data-testid="service-card">
              <img src="https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/0izpw2gc_fjztx3rd_image.png" alt="Well-Maintained Fleet" className="w-full h-48 object-cover" />
              <div className="p-6 text-center">
                <h3 className="font-bold text-xl mb-3">Well-Maintained Fleet</h3>
                <p className="text-gray-600">Modern fleet of tractors and chassis maintained to the highest standards. Regular inspections, preventive maintenance, and DOT compliance ensure reliable service.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-6 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div data-testid="stat-card">
              <div className="text-4xl font-bold mb-2">8+</div>
              <div className="text-blue-100">Years of Experience</div>
            </div>
            <div data-testid="stat-card">
              <div className="text-4xl font-bold mb-2">Hundreds</div>
              <div className="text-blue-100">Happy Clients</div>
            </div>
            <div data-testid="stat-card">
              <div className="text-4xl font-bold mb-2">Thousands</div>
              <div className="text-blue-100">Containers Moved</div>
            </div>
            <div data-testid="stat-card">
              <div className="text-4xl font-bold mb-2">5 States</div>
              <div className="text-blue-100">Service Areas</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Exceeding Your Expectations, Every Shipment</h2>
              <p className="text-base text-gray-600 mb-5">At Rob & Roberly Transport, we treat every container with the utmost care and professionalism.</p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">On-Time Delivery Guarantee</h3>
                    <p className="text-gray-600">98% on-time delivery rate with real-time tracking and proactive communication about any delays or issues.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Transparent Pricing</h3>
                    <p className="text-gray-600">No hidden fees or surprise charges. Clear, competitive pricing with detailed quotes provided upfront.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Responsive Support</h3>
                    <p className="text-gray-600">Dedicated dispatch and customer support during business hours. Quick response times and excellent communication.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img src="https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/db8t65ty_image.png" alt="Professional Logistics Operations" className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-8 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Comprehensive Northeast Coverage</h2>
            <p className="text-lg text-blue-100">Serving major ports across five states</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6 text-center">
            <div className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-2">New Jersey</h3>
              <p className="text-blue-100">Newark, Elizabeth, Jersey City, etc.</p>
            </div>
            <div className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-2">New York</h3>
              <p className="text-blue-100">NYC, Buffalo, Albany, etc.</p>
            </div>
            <div className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-2">Connecticut</h3>
              <p className="text-blue-100">Hartford, New Haven, Bridgeport, etc.</p>
            </div>
            <div className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-2">Pennsylvania</h3>
              <p className="text-blue-100">Philadelphia, Pittsburgh, Allentown, etc.</p>
            </div>
            <div className="bg-white bg-opacity-10 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-2">Delaware</h3>
              <p className="text-blue-100">Wilmington, Dover, Newark, etc.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section - Slightly Minimized */}
      <section className="py-8 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-full font-bold text-base mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google Reviews
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-900">Help Us Grow with Your Feedback!</h2>
              <p className="text-base text-gray-600">We're building our reputation one satisfied customer at a time</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-blue-900">Had a Great Experience?</h3>
                <p className="text-gray-700 mb-4 text-sm">Share your experience and help other businesses find reliable drayage services.</p>
                <a
                  href="https://share.google/NV8bKE3OfiLcKQ632"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition font-bold shadow-lg"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  Write a Google Review
                </a>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-green-900">Why Your Review Matters</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Helps businesses make informed decisions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Motivates us to maintain excellent service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Takes less than 2 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Supports local business excellence</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Minimized */}
      <section className="py-6 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold mb-1 text-gray-900">Frequently Asked Questions</h2>
            <p className="text-sm text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="space-y-2">
            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>What's your rate structure?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">Rates are calculated based on several factors, including distance, weight, volume, pickup/delivery location and current market conditions. We offer transparent pricing with itemized lists to help you avoid unexpected accessorial charges. Get an instant quote using our <Link to="/get-quote" className="text-blue-600 hover:text-blue-700 font-medium underline">calculator</Link> or contact us for a detailed estimate.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>How do I get started or request a quote?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">Getting started is easy. You can request a free quote through our <Link to="/get-quote" className="text-blue-600 hover:text-blue-700 font-medium underline">online calculator</Link> or contact a representative directly at <a href="tel:+19089520250" className="text-blue-600 hover:text-blue-700 font-medium">908-952-0250</a> / <a href="mailto:Rates@robandroberlytransport.com" className="text-blue-600 hover:text-blue-700 font-medium">Rates@robandroberlytransport.com</a></p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>Do you have your own chassis?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">Yes. We have a variety of chassis, including 40ft, 20ft, and triaxle to handle legal, overweight, and reefer shipments.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>Do you offer yard storage and warehousing / transloading?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">Yes, yard storage and warehousing/transloading services are available, which include secure, 24/7 yard storage surveillance for containers, and comprehensive transloading and warehousing services.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>What type of shipments do you handle?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">We handle two primary types of shipments: General freights (Imports and Exports). We also have experience handling refrigerated containers (Reefers) with proper monitoring and care throughout the transport process.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>How fast can you pick up my container?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">We guarantee same-day pickup service when you call us by 1pm. Our typical response time is around 15 minutes. Office hours: Monday through Friday, 8AM to 5PM.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>What areas do you serve?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">We serve the entire Northeast region including New Jersey, New York, Connecticut, Pennsylvania, and Delaware. We handle pickups from major ports like Newark, Elizabeth, and all NY/NJ terminals.</p>
            </details>

            <details className="bg-white rounded-lg p-3 group shadow-sm hover:shadow-md transition">
              <summary className="font-semibold text-sm cursor-pointer flex justify-between items-center">
                <span>Do you provide tracking for shipments?</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <p className="mt-2 text-gray-600 text-xs">Yes! All our trucks have GPS tracking. You'll receive real-time updates on your container's location and status via phone, email, or text message.</p>
            </details>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsCarousel />

      {/* CTA Section */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Ready to Get Started?</h2>
          <p className="text-lg text-gray-600 mb-6">Contact us today for reliable, cost-effective transportation solutions tailored to your business needs.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/get-quote" className="bg-blue-600 text-white px-10 py-4 rounded-md hover:bg-blue-700 transition font-medium text-lg" data-testid="cta-get-quote-btn">Get a Free Quote</Link>
            <a href="tel:+19089520250" className="bg-gray-800 text-white px-10 py-4 rounded-md hover:bg-gray-900 transition font-medium text-lg">Call (908) 952-0250</a>
            <Link to="/contact" className="bg-blue-600 text-white px-10 py-4 rounded-md hover:bg-blue-700 transition font-medium text-lg">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-6">About Rob & Roberly Transport</h2>
          <p className="text-xl max-w-3xl mx-auto">Leading the way in container drayage and logistics solutions across the Northeast since 2017</p>
        </div>
      </section>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Our Story */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">Founded in 2017, Rob & Roberly Transport was built on a simple principle: provide reliable, professional container drayage services with a personal touch. What started as a small operation with just a handful of drivers has grown into one of the Northeast's most trusted logistics partners.</p>
              <p className="text-gray-700 mb-4 text-lg leading-relaxed">Today, we serve hundreds of clients across five states, handling everything from single container moves to complex, high-volume logistics operations. Our success is built on the relationships we've developed with our clients, our commitment to excellence, and our unwavering dedication to getting the job done right.</p>
              <p className="text-gray-700 text-lg leading-relaxed">Whether you're a major corporation or a small business owner, you can count on Rob & Roberly Transport to treat your cargo with the same level of care and professionalism.</p>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1753579167765-d88ba3719f96?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Trucking Operations" className="rounded-lg shadow-2xl" />
            </div>
          </div>

          {/* Mission & Values */}
          <div className="bg-gray-50 rounded-lg p-12 mb-16">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">Our mission is to ensure that our customers' freight is delivered safely, securely, and in a timely manner. We provide up-to-date feedback about pickups and delivery times, giving you peace of mind throughout the entire process.</p>
                <p className="text-gray-700 text-lg leading-relaxed">We strive to be more than just a service provider—we aim to be a trusted partner in your supply chain, offering solutions that help your business grow and succeed.</p>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-6">Our Values</h2>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-blue-600 rounded-full p-1 mr-3 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Reliability</h3>
                      <p className="text-gray-600">We show up on time, every time, and deliver on our promises.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-600 rounded-full p-1 mr-3 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Safety First</h3>
                      <p className="text-gray-600">The safety of our drivers and your cargo is our top priority.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-600 rounded-full p-1 mr-3 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Transparency</h3>
                      <p className="text-gray-600">Open communication and honest pricing with no hidden fees.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-600 rounded-full p-1 mr-3 mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Customer Focus</h3>
                      <p className="text-gray-600">Your success is our success. We go the extra mile for every client.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Why Choose Rob & Roberly Transport?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src="https://images.unsplash.com/photo-1590496793907-4d66e2994b4d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Container Port Operations" className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-4">Experience That Matters</h3>
                  <p className="text-gray-600 mb-4">With over 8 years in the industry and thousands of containers successfully transported, we've seen it all and know how to handle any situation that arises.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Average 8+ years driver experience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>DOT certified and compliant</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>TWIC card certified drivers</span>
                  </li>
                </ul>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/hyv5vu7c_image.png" alt="Modern Trucking Technology" className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-4">Modern Technology</h3>
                  <p className="text-gray-600 mb-4">We leverage the latest logistics technology to provide you with real-time visibility and efficient operations.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>GPS tracking on all vehicles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Online rate calculator</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Automated status updates</span>
                  </li>
                </ul>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src="https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Comprehensive Coverage" className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-4">Comprehensive Coverage</h3>
                  <p className="text-gray-600 mb-4">We serve all major ports and distribution centers across five Northeastern states.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>NY/NJ Port Authority terminals</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Philadelphia port access</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>Regional distribution centers</span>
                  </li>
                </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Certifications & Compliance */}
          <div className="bg-blue-50 rounded-lg p-12">
            <h2 className="text-3xl font-bold mb-6 text-center">Certifications & Compliance</h2>
            <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto text-lg">We maintain the highest standards of safety and compliance in the industry. Our commitment to excellence is reflected in our certifications and safety record.</p>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="bg-white rounded-lg p-6 shadow">
                  <h3 className="font-bold text-xl mb-2">DOT Certified</h3>
                  <p className="text-gray-600">Fully compliant with Department of Transportation regulations</p>
                </div>
              </div>
              <div>
                <div className="bg-white rounded-lg p-6 shadow">
                  <h3 className="font-bold text-xl mb-2">Licensed & Insured</h3>
                  <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind</p>
                </div>
              </div>
              <div>
                <div className="bg-white rounded-lg p-6 shadow">
                  <h3 className="font-bold text-xl mb-2">TWIC Certified</h3>
                  <p className="text-gray-600">Transportation Worker Identification Credential approved</p>
                </div>
              </div>
              <div>
                <div className="bg-white rounded-lg p-6 shadow">
                  <h3 className="font-bold text-xl mb-2">Safety Rated</h3>
                  <p className="text-gray-600">Excellent safety rating from federal agencies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Services Page imported from separate file